import { DataTable } from "../../../../components/DataTable";
import { categoriesGroups, scopeThreeCategories } from "../helpers";
import { sortAlphabeticallyByAttribute } from "../../../../utils";
import {
  generatedWaste
} from "../../../OrganizationQuestionnaire/components/Categories/components/Category/helpers";
import { ScopeThreeCategoryThreeFactors } from "./index";
import { useTranslation } from "react-i18next";
import { Switch, Text } from "@chakra-ui/react";
import * as React from "react";
import { useCallback } from "react";
import { toggleConversionFactorEnabled } from "../../../../api/conversion-factors/conversion-factors";

function ScopeThreeFactorsGrid({ data, selectedCategory, organizationStructure }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "conversionFactors.headers"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const enabledColumn = {
    header: t("enabled"),
    accessorKey: "_enabled",
    size: 80,
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) => (
      <Switch
        size="sm"
        colorScheme="green"
        defaultChecked={true}
        onChange={(e) => toggleConversionFactorEnabled({
          scope: "scope3",
          typeId: row.original._typeId,
          typeName: row.original._typeName,
          enabled: e.target.checked,
        })}
      />
    ),
  };

  const getColumns = useCallback((category) => {
    switch (category.group) {
      case categoriesGroups.CATEGORY_ONE_AND_TWO: {
        return [
          enabledColumn,
          { header: t("productCategory"), accessorKey: "productCategory", size: 300 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 200, enableColumnFilter: false },
          { header: t("source"), accessorKey: "source", size: 150 }
        ];
      }
      case categoriesGroups.CATEGORY_FOUR_AND_NINE: {
        return [
          enabledColumn,
          { header: t("typeOfTransportation"), accessorKey: "type", size: 200 },
          { header: t("subtypeOfTransportation"), accessorKey: "subtype", size: 220 },
          { header: t("subtypeTwoOfTransportation"), accessorKey: "subtype2", size: 220 },
          { header: t("unit"), accessorKey: "unit", size: 150 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 300, enableColumnFilter: false },
          { header: t("source"), accessorKey: "source", size: 150 }
        ];
      }
      case categoriesGroups.CATEGORY_FIVE_AND_TWELVE: {
        return [
          enabledColumn,
          { header: t("wasteType"), accessorKey: "wasteType", size: 200 },
          { header: t("disposalMethod"), accessorKey: "disposalMethod", size: 200 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 200, enableColumnFilter: false },
          { header: t("source"), accessorKey: "source", size: 150 }
        ];
      }
      case categoriesGroups.CATEGORY_SIX_AND_SEVEN: {
        return [
          enabledColumn,
          { header: t("modeOfTransport"), accessorKey: "modeOfTransport", size: 150 },
          { header: t("transportSubtype"), accessorKey: "transportSubtype", size: 200 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 200, enableColumnFilter: false },
          { header: t("source"), accessorKey: "source", size: 150 }
        ];
      }
      case categoriesGroups.CATEGORY_ELEVEN: {
        return [
          enabledColumn,
          { header: t("type"), accessorKey: "type", size: 200 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 200, enableColumnFilter: false }
        ];
      }
      default:
        return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const getMappedData = useCallback((category) => {
    switch (category.group) {
      case categoriesGroups.CATEGORY_ONE_AND_TWO: {
        const products = data[category.group].map(el => {
          return {
            productCategory: `${el.productCategory.code} - ${el.productCategory.name}`,
            conversionFactor: el.conversionFactor,
            source: el.source ?? ""
          };
        });
        // Cat 1 and Cat 2 share a group; only Cat 1 has a water usage factor.
        if (category.value === "categoryOne" && data.waterUsage != null) {
          return [{ productCategory: t("waterUsage"), conversionFactor: data.waterUsage, source: "" }, ...products];
        }
        return products;
      }
      case categoriesGroups.CATEGORY_FOUR_AND_NINE: {
        return data[category.group]
          .map(el => {
            return {
              type: el.typeOfTransport.name,
              subtype: el.subtypeOfTransport.name,
              subtype2: el.subtypeTwo.name,
              unit: el.unit.name,
              conversionFactor: el.conversionFactor,
              source: el.source ?? ""
            };
          })
          .sort((a, b) => sortAlphabeticallyByAttribute(a, b, "type"));
      }
      case categoriesGroups.CATEGORY_FIVE_AND_TWELVE: {
        const findWasteType = (type) => {
          const found = generatedWaste.find(el => Object.keys(el.types).includes(type));
          if (typeof found !== "undefined") {
            return globalTranslation(`organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.wasteType.options.${found.activity}.${type}`, { defaultValue: type });
          }
          return type;
        };

        const wasteRows = data[category.group]
          .map(el => {
            return {
              wasteType: findWasteType(el.wasteType),
              disposalMethod: globalTranslation(`organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.disposalMethod.options.${el.disposalMethod}`, { defaultValue: el.disposalMethod }),
              conversionFactor: el.conversionFactor,
              source: el.source ?? ""
            };
          })
          .sort((a, b) => sortAlphabeticallyByAttribute(a, b, "wasteType"));
        // Cat 5 and Cat 12 share a group; only Cat 5 has a wastewater factor.
        if (category.value === "categoryFive" && data.wastewater != null) {
          return [{ wasteType: t("wastewater"), disposalMethod: "", conversionFactor: data.wastewater, source: "" }, ...wasteRows];
        }
        return wasteRows;
      }
      case categoriesGroups.CATEGORY_SIX_AND_SEVEN: {
        return data[category.group]
          .map(el => {
            return {
              modeOfTransport: globalTranslation(`organizationQuestionnaire.scope3.inputs.modeOfTransportOptions.${el.modeOfTransport}`, { defaultValue: el.modeOfTransport }),
              transportSubtype: el.transportSubtype.toLowerCase() === "walking" ||
              el.transportSubtype.toLowerCase() === "cycling" ? "" :
                globalTranslation(`organizationQuestionnaire.scope3.inputs.transportSubtypeOptions.${el.modeOfTransport}.${el.transportSubtype}`, { defaultValue: el.transportSubtype }),
              conversionFactor: el.conversionFactor,
              source: el.source ?? ""
            };
          })
          .sort((a, b) => sortAlphabeticallyByAttribute(a, b, "modeOfTransport"));
      }
      case categoriesGroups.CATEGORY_ELEVEN: {
        if (!data[category.group]["soldElectricityConversionFactor"] && !data[category.group]["soldGasConversionFactor"]) {
          return [];
        } else {
          return [
            { type: t("soldElectricity"), conversionFactor: data[category.group]["soldElectricityConversionFactor"] },
            { type: t("soldGas"), conversionFactor: data[category.group]["soldGasConversionFactor"] }
          ];
        }
      }
      default:
        return [];
    }
  }, [data, globalTranslation, t]);

  const renderContent = () => {
    const category = scopeThreeCategories.find(el => el.value === selectedCategory);
    if (typeof category !== "undefined") {
      if (category.group === categoriesGroups.CATEGORY_THREE) {
        return <ScopeThreeCategoryThreeFactors data={data} organizationStructure={organizationStructure} />;
      } else {
        return !getMappedData(category) || getMappedData(category).length === 0 ?
          <Text fontSize="lg" fontWeight="bold">
            {globalTranslation("noDataAvailable")}
          </Text> :
          <DataTable
            key={category.value}
            data={getMappedData(category)}
            columns={getColumns(category)}
            enableFilters={true}
          />;
      }
    }
    return null;
  };

  return renderContent();
}

export default ScopeThreeFactorsGrid;